import { useCallback, useMemo } from "react";
import type { FieldPath, FieldValues, UseFormReturn } from "react-hook-form";

/**
 * Lo que un formulario por pasos necesita saber de sus propios errores.
 *
 * Las tres respuestas salen del MISMO reparto de campos —qué campo vive
 * en qué paso—, y por eso se calculan juntas: si "Siguiente" validara una
 * lista y el foco mirara otra, un paso podría frenar por un campo que
 * luego nadie sabe encontrar.
 */
export interface StepFields {
  /**
   * El permiso para avanzar: valida los campos de ESE paso y nada más.
   * Es lo que se le pasa a `useWizard` como `canAdvance`.
   */
  validateStep: (step: number) => Promise<boolean>;
  /**
   * Pone el foco en el primer campo con error del paso. Devuelve `true`
   * si lo movió; `false` si el paso no tiene ninguno y el foco le toca
   * entonces a la región.
   */
  focusStepError: (step: number) => boolean;
  /** El primer paso que tiene algún error, o `-1` si no hay ninguno. */
  firstStepWithError: () => number;
}

/**
 * ═══════════════════════════════════════════════════════════════════
 * El puente entre react-hook-form y un asistente: qué se valida al
 * avanzar, dónde va el foco y a qué paso lleva un guardado que falló.
 *
 * ── Por qué vive aquí y no en la pantalla ───────────────────────────
 * Son tres respuestas idénticas en cualquier formulario largo por pasos
 * —el del producto y el de sus detalles editoriales, hoy— y las tres se
 * derivan de una sola cosa: la lista de campos de cada paso. Escritas en
 * cada pantalla, la primera vez que una cambie la otra se queda atrás y
 * nadie lo nota hasta que alguien navega con el teclado.
 *
 * ── Validar solo lo que ya se vio ───────────────────────────────────
 * `trigger` con una lista de nombres corre el esquema COMPLETO pero solo
 * pinta —y solo reporta— los errores de esos nombres. Es exactamente lo
 * que hace falta: una regla que mira dos campos de pasos distintos se
 * comprueba de verdad, pero el nombre vacío del paso 1 no aparece en rojo
 * mientras se escriben las medidas del paso 7.
 * ═══════════════════════════════════════════════════════════════════
 *
 * @param form   El formulario, tal cual lo devuelve `useForm`.
 * @param fieldsOfStep Los campos CONCRETOS de un paso, ya expandidos: si
 *   el paso cubre un mapa abierto (`prices.MXN`, `prices.EUR`…), la lista
 *   trae las llaves que existen ahora. Pasar el prefijo a secas dejaría
 *   que "Siguiente" dependiera de cómo react-hook-form resuelve una ruta
 *   que apunta a un objeto entero.
 * @param total  Cuántos pasos hay. Acota la búsqueda del paso con error.
 */
export function useStepFields<TValues extends FieldValues, TContext, TOut>(
  form: UseFormReturn<TValues, TContext, TOut>,
  fieldsOfStep: (step: number) => string[],
  total: number,
): StepFields {
  const validateStep = useCallback(
    (step: number) => form.trigger(fieldsOfStep(step) as FieldPath<TValues>[]),
    [fieldsOfStep, form],
  );

  const erroredFieldOf = useCallback(
    (step: number) =>
      fieldsOfStep(step).find(
        (field) => form.getFieldState(field as FieldPath<TValues>).error,
      ),
    [fieldsOfStep, form],
  );

  const focusStepError = useCallback(
    (step: number) => {
      const errored = erroredFieldOf(step);
      if (!errored) return false;
      form.setFocus(errored as FieldPath<TValues>);
      return true;
    },
    [erroredFieldOf, form],
  );

  const firstStepWithError = useCallback(() => {
    for (let step = 0; step < total; step += 1) {
      if (erroredFieldOf(step)) return step;
    }
    return -1;
  }, [erroredFieldOf, total]);

  return useMemo(
    () => ({ validateStep, focusStepError, firstStepWithError }),
    [firstStepWithError, focusStepError, validateStep],
  );
}
